import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient';
import { toast } from 'sonner';
import { Database } from '@/_constants/database.types';

type PastPaperRow = Database['public']['Tables']['past_papers']['Row'];
type QuestionRow = Database['public']['Tables']['questions']['Row'];

export type AdminPastPaperDetail = PastPaperRow & {
  questions: QuestionRow[];
};

const fetchAdminPastPaper = async (id: string): Promise<AdminPastPaperDetail> => {
  const { data, error } = await supabaseClient
    .from('past_papers')
    .select(`
      *,
      questions (*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching past paper:', error);
    throw error;
  }

  // Sort questions by question_order
  const sortedQuestions = (data.questions || []).sort(
    (a: QuestionRow, b: QuestionRow) => (a.question_order ?? 0) - (b.question_order ?? 0)
  );

  return {
    ...data,
    questions: sortedQuestions,
  };
};

export const useAdminPastPaper = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin-past-paper', id],
    queryFn: () => fetchAdminPastPaper(id!),
    enabled: !!id,
  });
};

// Publish all questions for a past paper
const publishPastPaperQuestions = async (pastPaperId: string): Promise<void> => {
  // Update all questions to published
  const { error: questionsError } = await supabaseClient
    .from('questions')
    .update({ status: 'published' })
    .eq('past_paper_id', pastPaperId);

  if (questionsError) {
    console.error('Error publishing questions:', questionsError);
    throw questionsError;
  }

  // Update past paper status to published
  const { error: paperError } = await supabaseClient
    .from('past_papers')
    .update({ status: 'published' })
    .eq('id', pastPaperId);

  if (paperError) {
    console.error('Error publishing past paper:', paperError);
    throw paperError;
  }
};

export const usePublishPastPaperQuestions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: publishPastPaperQuestions,
    onSuccess: (_, pastPaperId) => {
      toast.success('Past paper and questions published successfully!');
      queryClient.invalidateQueries({ queryKey: ['admin-past-paper', pastPaperId] });
      queryClient.invalidateQueries({ queryKey: ['admin-past-papers'] });
    },
    onError: () => {
      toast.error('Failed to publish. Please try again.');
    },
  });
};

// Update a single question
interface UpdateQuestionParams {
  questionId: string;
  pastPaperId: string;
  updates: {
    content?: string;
    status?: 'draft' | 'published' | 'archived';
    mark_scheme_content?: unknown;
    image_page_numbers?: number[] | null;
    reading_insert_page_numbers?: number[] | null;
  };
}

const updateQuestion = async ({ questionId, updates }: UpdateQuestionParams): Promise<void> => {
  const { error } = await supabaseClient
    .from('questions')
    .update(updates)
    .eq('id', questionId);

  if (error) {
    console.error('Error updating question:', error);
    throw error;
  }
};

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateQuestion,
    onSuccess: (_, { pastPaperId }) => {
      toast.success('Question updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-past-paper', pastPaperId] });
    },
    onError: () => {
      toast.error('Failed to update question. Please try again.');
    },
  });
};

export default useAdminPastPaper;
